<template>
  <div class="startup-templates-edition basic-gray-section">
    <h1 class="basic-blue-title">
      {{ $t('message.CONFIG_STARTUP_TEMPLATES') }}
    </h1>

    <div class="f-h-below-title">
      <div class="startup-templates-edition__list">
        <div
          v-for="tmp in templatesCopy"
          :key="tmp.id"
          class="startup-templates-edition__item settings-dropdown"
          :class="{ expand: isExpandedPerId[`${tmp.id}`] }"
        >
          <div
            class="settings-item"
            @click="$set(isExpandedPerId, `${tmp.id}`, !isExpandedPerId[`${tmp.id}`])"
          >
            <Checkbox
              :value="isSelectedPerId[`${tmp.id}`]"
              class="clickable-hoverable"
              stop-propagation
              @input="$set(isSelectedPerId, `${tmp.id}`, $event)"
            />
            <Icon :w="12" class="expand-img">{{ wideArrowRightMedium }}</Icon>
            <div class="startup-templates-edition__item__details">
              <EditableLabel
                v-model="tmp.name"
                class="clickable-hoverable"
                :placeholder="$t('message.CONFIG_SUT_NAME_PH')"
                :legend="`${$t('message.CONFIG_SUT_NAME_PH')}${$t('message.TWO_DOTS')}`"
                @input="edit(tmp, 'name')"
              />
              <div class="v-centerer field-hint">
                <span>{{ $tc('message.CONFIG_SUT_N_SCHEMES', tmp.schemes.length, { n: tmp.schemes.length }) }}</span>
                <span class="startup-templates-edition__item__dash">-</span>
                <span>{{ $tc('message.CONFIG_SUT_N_PAGES', tmp.pages.length, { n: tmp.pages.length }) }}</span>
                <span class="startup-templates-edition__item__dash">-</span>
                <span>
                  {{ $tc('message.CONFIG_SUT_N_PLANNINGS', tmp.plannings.length, { n: tmp.plannings.length }) }}
                </span>
              </div>
            </div>
            <Icon class="delete-img" @click.native.stop="deleteItem(tmp)">{{ deleteMedium }}</Icon>
          </div>
          <TransitionExpand>
            <div v-if="isExpandedPerId[`${tmp.id}`]" class="startup-templates-edition__item__content">
              <EditableLabelRadio
                v-model="tmp.plans"
                class="clickable-hoverable"
                :items="actionPlans"
                label-key="name"
                :placeholder="$t('message.CLICK_TO_CHANGE')"
                :legend="`${$t('message.ACTION_PLANS')}${$t('message.TWO_DOTS')}`"
                multiple
                @input="edit(tmp, 'plans')"
              />
              <EditableLabelRadio
                v-model="tmp.schemes"
                class="clickable-hoverable"
                :items="allSchemes"
                label-key="name"
                :placeholder="$t('message.CLICK_TO_CHANGE')"
                :legend="`${$t('message.CONFIG_INDIC_SCHEMES')}${$t('message.TWO_DOTS')}`"
                multiple
                @input="edit(tmp, 'schemes')"
              />
              <EditableLabelRadio
                v-model="tmp.pages"
                class="clickable-hoverable"
                :items="allCfgPages"
                label-key="name"
                :placeholder="$t('message.CLICK_TO_CHANGE')"
                :legend="`${$t('message.CFG_PAGES')}${$t('message.TWO_DOTS')}`"
                multiple
                @input="edit(tmp, 'pages')"
              />
              <EditableLabelRadio
                v-model="tmp.plannings"
                class="clickable-hoverable"
                :items="allPlannings"
                label-key="name"
                :placeholder="$t('message.CLICK_TO_CHANGE')"
                :legend="`${$t('message.SETTINGS_MEETINGS')}${$t('message.TWO_DOTS')}`"
                multiple
                @input="edit(tmp, 'plannings')"
              />
            </div>
          </TransitionExpand>
        </div>
        <button class="add-button startup-templates-edition__new" @click="SET_STARTUP_TEMPLATE()">
          <Icon>{{ addCircleMedium }}</Icon>
          <span>{{ $t('message.CONFIG_SUT_NEW') }}</span>
        </button>
      </div>
      <div class="h-v-centerer startup-templates-edition__apply">
        <button class="action-btn" :disabled="selectedTemplates.length === 0" @click="$emit('ok', selectedTemplates)">
          {{ $t('message.CONFIG_SUT_APPLY_SELECTED') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Component to edit Start-up templates and select them.
 * A start-up template is a set of configurable pages, indicator schemes and meeting plannings
 * editable in the config section of Shizen.
 * This allows the administrator to apply this data directly to multiple at once.
 */

import toastr from 'toastr';
import { cloneDeep } from 'lodash';
import { mapState, mapActions } from 'vuex';
import { GET_STARTUP_TEMPLATES, GET_SCHEMES, GET_CONFIG_ACTION_PLANS } from '@/store/modules/config/mutation-types';
import {
  FETCH_STARTUP_TEMPLATES,
  SET_STARTUP_TEMPLATE,
  DELETE_STARTUP_TEMPLATE,
} from '@/store/modules/config/action-types';

import { GET_PLANNINGS } from '@/store/modules/meeting/mutation-types';
// Components
import Checkbox from '@/components/checkbox.vue';
import EditableLabel from '@/components/editable-label.vue';
import EditableLabelRadio from '@/components/editable-label-radio.vue';
import Icon from '@/components/icon.vue';
import TransitionExpand from '@/components/transition-expand.vue';
// Utils & assets
import { calert } from '@/utils/main';
import { addCircleMedium, deleteMedium, wideArrowRightMedium } from '@/assets/icons';


export default {

  name: 'StartupTemplatesEdition',

  components: {
    Checkbox,
    EditableLabel,
    EditableLabelRadio,
    Icon,
    TransitionExpand,
  },

  data() {
    return {
      addCircleMedium,
      deleteMedium,
      wideArrowRightMedium,
      allCfgPages: [],
      templatesCopy: [],
      isExpandedPerId: {},
      isSelectedPerId: {},
    };
  },

  computed: {
    /** Boolean: True if at least one template is selected */
    atLeastOneTemplateSelected() {
      const selected = this.templates.filter(tmp => this.isSelectedPerId[`${tmp.id}`]);
      return selected.length > 0;
    },
    /** Array: Selected templates */
    selectedTemplates() {
      return this.templates.filter(tmp => this.isSelectedPerId[`${tmp.id}`]);
    },
    ...mapState('config', {
      allSchemes: state => state[GET_SCHEMES.stateKey] || [],
      templates: state => state[GET_STARTUP_TEMPLATES.stateKey] || [],
      templatesLoading: state => state[GET_STARTUP_TEMPLATES.loadingKey],
      actionPlans: state => state[GET_CONFIG_ACTION_PLANS.stateKey] || [],
    }),
    ...mapState('meeting', {
      allPlannings: state => state[GET_PLANNINGS.stateKey] || [],
    }),
  },

  watch: {
    templates: {
      deep: true,
      handler() {
        this.templatesCopy = cloneDeep(this.templates);
      },
    },
  },

  created() {
    this.refresh();
  },

  methods: {
    /** Fetch the company's start-up templates */
    refresh() {
      if (!this.templatesLoading) {
        this.FETCH_STARTUP_TEMPLATES();
      }
      // TODO: Use a store action and fetch these from config/teams instead
      this.$http.post('/server/customPages/get.php').then((response) => {
        if (response && response.data instanceof Array) {
          this.allCfgPages = response.data;
        } else {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
        }
      });
    },
    /** Save the edited template and refresh the list */
    edit(template, param) {
      this.SET_STARTUP_TEMPLATE({ id: template.id, [param]: template[param] });
    },
    /** Prompts the user to confirm the template deletion */
    deleteItem(template) {
      calert(this.$t('message.CONFIG_SUT_CONFIRM_DELETE'), [
        {
          title: this.$t('message.DELETE'),
          sClass: 'red',
          action: () => {
            this.confirmDeleteItem(template);
          },
        },
        { title: this.$t('message.CANCEL') },
      ]);
    },
    /** Sends the deletion XHR */
    confirmDeleteItem(template) {
      this.DELETE_STARTUP_TEMPLATE({ id: template.id });
    },
    ...mapActions('config', {
      FETCH_STARTUP_TEMPLATES,
      SET_STARTUP_TEMPLATE,
      DELETE_STARTUP_TEMPLATE,
    }),
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.startup-templates-edition {

  &__item {
    &__details {
      flex: 1;
    }

    .delete-img {
      flex: 0 0 auto;
      padding: 0.5em;

      &:hover {
        background: $color-hoverbtn-red;
      }
    }

    &__content {
      padding: 1em 2em;
      background: $basic-section-background-light;

      .editable-label {
        padding: 0.3em;
        margin: 0em 0;
      }
    }
    &__dash {
      margin: 0 1em;
    }

    &.settings-dropdown .settings-item {
      height: auto;
      padding: 0.5em 1em;

      .checkbox {
        padding: 0.5em;
        margin-right: 0.5em;

        .tick {
          margin: 0;
        }
      }
    }
  }

  &__new {
    margin: 0.7em 0 5em;
  }

  .f-h-below-title {
    display: flex;
    flex-flow: column;
  }

  &__list {
    flex: 1;
    overflow: auto;
  }

  &__apply {
    flex: 0 0 auto;
    padding: 0.8em;
  }
}
</style>
